import { FC, Suspense, useMemo } from 'react';
import UnitsPage from './UnitsPage';
import { Unit } from '../../models/Unit';
import { useAppSelector } from '../../redux/hooks';
import { useResolvedUnit } from '../../custom-hooks/useResolvedUnit';
import { MainFallback } from '../Fallback/MainFallBack';
import { selectedUnit } from '../../redux/reducers/unitSlice';

interface Props {
  currentTargets: ReadonlyArray<{
    id: string;
    name?: string | null;
    summary?: string | null;
  }>;
}

const UnitPageContainer: FC<Props> = ({ currentTargets }) => {
  const unitRedux = useAppSelector(selectedUnit);

  // Filter units by target group and pass to hook for better accuracy
  const unitsInTargetGroup = currentTargets as Unit[];

  // Focus-aware current unit: the teacher-set focus while it is incomplete,
  // otherwise the first-incomplete progress default. Drives the "Jump back in" card.
  const resolvedUnit = useResolvedUnit(unitsInTargetGroup);

  const enhancedUnits = useMemo(() => {
    if (!currentTargets?.length) return [];

    const sortedUnits = [...currentTargets].sort((a, b) => {
      // Convert names to numbers for proper numeric sorting
      const nameA = parseInt(a.name || '0', 10);
      const nameB = parseInt(b.name || '0', 10);
      return nameA - nameB;
    });

    return sortedUnits.map(unit => {
      const isActiveUnit = unit.id === unitRedux?.id;
      const isFirstUnfinishedUnit = unit.id === resolvedUnit?.id;

      // Determine button text
      let buttonText = 'Explore Unit';
      if (isFirstUnfinishedUnit) {
        buttonText = 'Jump Back In';
      }

      return {
        ...unit,
        isActive: isActiveUnit,
        buttonText,
        isFirstUnfinished: isFirstUnfinishedUnit,
      };
    });
  }, [currentTargets, unitRedux, resolvedUnit]);

  return resolvedUnit ? (
    <Suspense fallback={<MainFallback />}>
      <UnitsPage allUnits={enhancedUnits as Unit[]} loadQuerySkillsByTarget={() => {}} defaultUnit={resolvedUnit} />;
    </Suspense>
  ) : (
    <div className="tw-flex tw-justify-center tw-items-center tw-p-8">
      <div className="tw-text-center">
        <div className="tw-text-lg tw-font-semibold tw-mb-2">Calculating your next unit...</div>
        <div className="tw-text-sm tw-text-gray-600">Finding the best unit for you to continue learning</div>
      </div>
    </div>
  );
};

export default UnitPageContainer;
